import { Linking, Platform } from 'react-native';
import {
	REQUEST,
	SUCCESS,
	FAILED,
	GET_ERRORS,
	SHOW_AUTH_MODAL,
	RESET_APP,
	SHOW_PASSWORD_RESET_MODAL,
	TOGGLE_OTP,
	AWAITINGMFA,
	SHOW_LOADING_MODAL,
	HIDE_LOADING_MODAL,
	INITIALISE_MENU,
	SHOW_OTP,
	DECLINE_TERMS,
	SET_LAST_VALID_USER_SESSION,
} from '@reduxLocal/actions/types';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import { LOGIN_API_URL } from './constant';
import { buildDispatchData } from './utils';
import storeData from '@utils/storeUser';
import { api, noCreds } from './api';
import {
	getRememberMePreference,
	persistAuthSession,
	saveRememberMePreference,
} from '@utils/persistentSessionStorage';
import { computeSessionExpireAt } from '@utils/sessionConstants';
import {
	attemptOfflineLoginFallback,
} from './utils/offlineLoginFallback';
import { isLikelyServerUnreachableError } from './utils/serverReachability';
import { getNetworkStatus, refreshNetworkStatus } from './utils/networkManager';
import navigate from '../src/navigation/rootNavigation';
import { markRecentAuthSuccess } from '../src/utils/authGracePeriod';

export const loginWithCachedSession = (cb) => {
	return async (dispatch) => {
		store.dispatch({ type: SHOW_LOADING_MODAL });
		try {
			await attemptOfflineLoginFallback(dispatch, cb);
		} catch (error) {
			store.dispatch({ type: HIDE_LOADING_MODAL });
			console.error('Offline cached login failed:', error);
		}
	};
};

const loginUser = (loginValues, cb) => {
	return async (dispatch) => {
		store.dispatch({ type: SHOW_LOADING_MODAL });
		dispatch(buildDispatchData(REQUEST));

		if (!loginValues.email || !loginValues.password) {
			store.dispatch({ type: HIDE_LOADING_MODAL });
			store.dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content:
						'Please ensure both, Email and Password fields are not empty.',
					type: 'error',
				},
			});
			return;
		}

		// Check if learning mode is enabled from Redux state
		const state = store.getState();
		const isLearningMode = state?.app?.learningMode || false;
		const rememberMe =
			loginValues.rememberMe ?? getRememberMePreference();

		try {
			const currentNetworkStatus = await refreshNetworkStatus();

			// Include learning mode in login request
			const loginPayload = {
				...loginValues,
				learningMode: isLearningMode,
				rememberMe,
			};

			if (!currentNetworkStatus) {
				await attemptOfflineLoginFallback(dispatch, cb);
				return;
			}

			// Use `api` (credentials by default) so the browser stores the httpOnly session cookie.
			const response = await api.post(LOGIN_API_URL, loginPayload, {
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				withCredentials: true,
				validateStatus: () => true,
			});
			const statusCode = response.status;
			const obj = response.data;

			if (statusCode === 204) {
				store.dispatch({ type: HIDE_LOADING_MODAL });
				const email = String(loginValues.email || '').trim().toLowerCase();
				if (Platform.OS === 'web') {
					try {
						await noCreds.post(`/password-reset/${encodeURIComponent(email)}`, null, {
							validateStatus: () => true,
						});
					} catch (e) {
						console.warn('[login] password reset send after init_required failed', e);
					}
					await navigate('Auth', {
						screen: 'SetPassword',
						params: { email },
					});
					return;
				}
				store.dispatch({ type: SHOW_PASSWORD_RESET_MODAL });
				return;
			}

			if (statusCode === 202) {
				if (Platform.OS === 'android') {
					Linking.openURL('otpauth://');
				}
				store.dispatch({ type: HIDE_LOADING_MODAL });
				store.dispatch({ type: SHOW_OTP });
				return;
			}

			if (statusCode === 404) {
				store.dispatch({ type: HIDE_LOADING_MODAL });
				store.dispatch({
					type: SHOW_AUTH_MODAL,
					data: {
						content: obj.error || 'An error occurred logging in',
						type: 'error',
					},
				});
				return;
			}

			if (statusCode !== 200) {
				if (
					statusCode === 502 ||
					statusCode === 503 ||
					statusCode === 504
				) {
					await attemptOfflineLoginFallback(dispatch, cb);
					return;
				}
				if (loginValues.token) {
					store.dispatch({ type: HIDE_LOADING_MODAL });
					store.dispatch({ type: SHOW_OTP });
					return;
				}
				store.dispatch({ type: HIDE_LOADING_MODAL });
				store.dispatch({
					type: SHOW_AUTH_MODAL,
					data: {
						content:
							obj?.data ||
							obj?.error ||
							'An error occurred logging in',
						type: 'error',
					},
				});
			} else if (statusCode === 200) {
				const { data } = obj;
				let amendedData = data;
				amendedData.success = true;
				amendedData.rememberMe = rememberMe;
				amendedData.expireAt = computeSessionExpireAt(rememberMe);
				saveRememberMePreference(rememberMe);
				await persistAuthSession({
					rememberMe,
					userId: amendedData?.userData?.id,
					sessionData: amendedData,
				});
				// Suppress session-expiry logout races during post-login navigation
				markRecentAuthSuccess();
				store.dispatch({ type: HIDE_LOADING_MODAL });
				dispatch(buildDispatchData(SUCCESS, amendedData));
				dispatch({
					type: SET_LAST_VALID_USER_SESSION,
					payload: amendedData,
				});
				store.dispatch({ type: INITIALISE_MENU });
				cb(data, true);
			}
		} catch (err: any) {
			if (
				!getNetworkStatus() ||
				isLikelyServerUnreachableError(err)
			) {
				const fellBack = await attemptOfflineLoginFallback(
					dispatch,
					cb
				);
				if (fellBack) return;
			}

			store.dispatch({ type: HIDE_LOADING_MODAL });
			console.error(err);
		}
	};
};

export default loginUser;
